import { useState, useRef, useCallback, useEffect } from 'react';

interface Props {
  show: boolean;
  onClose: () => void;
  onUpload: (files: File[]) => void;
}

export default function UploadModal({ show, onClose, onUpload }: Props) {
  const [dragOver, setDragOver] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dragOver) setDragOver(true);
  }, [dragOver]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    
    const items = e.dataTransfer.items;
    if (!items) {
      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        setSelectedFiles(prev => [...prev, ...files]);
      }
      return;
    }
    
    const files: File[] = [];
    const promises: Promise<void>[] = [];
    
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === 'file') {
        const entry = item.webkitGetAsEntry?.() || (item as any).getAsEntry?.();
        if (entry) {
          promises.push(traverseFileTree(entry, '', files));
        } else {
          const file = item.getAsFile();
          if (file) files.push(file);
        }
      }
    }
    
    await Promise.all(promises);
    if (files.length > 0) {
      setSelectedFiles(prev => [...prev, ...files]);
    }
  }, []);
  
  // Helper para recorrer el árbol de archivos/carpetas
  const traverseFileTree = async (entry: any, path: string, files: File[]): Promise<void> => {
    return new Promise((resolve) => {
      if (entry.isFile) {
        entry.file((file: File) => {
          // Crear un nuevo File con webkitRelativePath para mantener la estructura
          const newFile = new File([file], file.name, { type: file.type });
          Object.defineProperty(newFile, 'webkitRelativePath', {
            value: path + file.name,
            writable: false
          });
          files.push(newFile);
          resolve();
        });
      } else if (entry.isDirectory) {
        const dirReader = entry.createReader();
        dirReader.readEntries(async (entries: any[]) => {
          const subPromises = entries.map(subEntry => 
            traverseFileTree(subEntry, path + entry.name + '/', files)
          );
          await Promise.all(subPromises);
          resolve();
        });
      } else {
        resolve();
      }
    });
  };

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setSelectedFiles(prev => [...prev, ...files]);
    }
    e.target.value = '';
  }, []);

  const handleFolderSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setSelectedFiles(prev => [...prev, ...files]);
    }
    e.target.value = '';
  }, []);

  const handleUploadClick = useCallback(() => {
    if (selectedFiles.length > 0) {
      onClose();
      setTimeout(() => onUpload(selectedFiles), 100);
      setSelectedFiles([]);
    }
  }, [selectedFiles, onClose, onUpload]);

  // Reset selected files when modal opens/closes
  useEffect(() => {
    if (!show) {
      setSelectedFiles([]);
    }
  }, [show]);

  const handleBrowseClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFolderBrowseClick = useCallback(() => {
    folderInputRef.current?.click();
  }, []);

  if (!show) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="modal-backdrop fade show"
        style={{ zIndex: 1040 }}
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className="modal fade show d-block"
        style={{ zIndex: 1050 }}
        onClick={onClose}
        onDragOver={(e) => e.stopPropagation()}
        onDragEnter={(e) => e.stopPropagation()}
        onDragLeave={(e) => e.stopPropagation()}
        onDrop={(e) => e.stopPropagation()}
      >
        <div
          className="modal-dialog modal-dialog-centered"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-content" style={{ border: 'none', borderRadius: 12 }}>
            {/* Header */}
            <div className="modal-header" style={{ borderBottom: '1px solid #e2e8f0' }}>
              <h5 className="modal-title fw-semibold" style={{ color: '#1e293b' }}>
                <i className="bi bi-cloud-upload me-2" style={{ color: '#2563eb' }} />
                Subir archivos
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                aria-label="Cerrar"
              />
            </div>

            {/* Body */}
            <div className="modal-body p-4">
              {/* Drag & Drop Zone - Always visible */}
              <div
                className={`border-2 border-dashed rounded-3 text-center transition-all ${
                  dragOver ? 'border-primary bg-primary bg-opacity-10' : 'border-secondary'
                }`}
                style={{
                  borderColor: dragOver ? '#2563eb' : '#cbd5e1',
                  backgroundColor: dragOver ? 'rgba(37, 99, 235, 0.05)' : 'transparent',
                  transition: 'all 0.2s ease',
                  cursor: 'pointer',
                  padding: selectedFiles.length > 0 ? '1.5rem' : '3rem',
                }}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={handleBrowseClick}
              >
                {selectedFiles.length === 0 ? (
                  <>
                <i
                  className="bi bi-cloud-arrow-up d-block mb-3"
                  style={{
                    fontSize: 64,
                    color: dragOver ? '#2563eb' : '#94a3b8',
                    transition: 'color 0.2s ease',
                  }}
                />
                <h6 className="fw-semibold mb-2" style={{ color: '#1e293b' }}>
                  {dragOver ? 'Suelta los archivos aquí' : 'Arrastra archivos aquí'}
                </h6>
                <p className="small mb-3" style={{ color: '#64748b' }}>
                  o haz clic para seleccionar archivos
                </p>
                <div className="d-flex gap-2 justify-content-center">
                  <button
                    className="btn btn-feebbo"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleBrowseClick();
                    }}
                  >
                    <i className="bi bi-file-earmark me-2" />
                    Archivos
                  </button>
                  <button
                    className="btn btn-feebbo-outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleFolderBrowseClick();
                    }}
                  >
                    <i className="bi bi-folder2-open me-2" />
                    Carpeta
                  </button>
                </div>
                    <p className="small mt-3 mb-0" style={{ color: '#94a3b8' }}>
                      Tamaño máximo: 5 GB por archivo
                    </p>
                    <p className="small mb-0" style={{ color: '#f59e0b' }}>
                      ⚠️ Archivos mayores a 3 GB: se recomienda comprimir
                    </p>
                  </>
                ) : (
                  <>
                    <i
                      className="bi bi-cloud-arrow-up d-block mb-2"
                      style={{
                        fontSize: 32,
                        color: dragOver ? '#2563eb' : '#94a3b8',
                        transition: 'color 0.2s ease',
                      }}
                    />
                    <p className="mb-0 small fw-medium" style={{ color: '#64748b' }}>
                      {dragOver ? 'Suelta para añadir más archivos' : 'Arrastra más archivos aquí o haz clic'}
                    </p>
                  </>
                )}
              </div>

              {/* File List - Only when files selected */}
              {selectedFiles.length > 0 && (
                <div className="mt-3">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <h6 className="mb-0 fw-semibold" style={{ color: '#1e293b' }}>
                      Archivos seleccionados ({selectedFiles.length})
                    </h6>
                    <button
                      className="btn btn-sm btn-feebbo-outline"
                      onClick={() => setSelectedFiles([])}
                    >
                      <i className="bi bi-x-circle me-1" />
                      Limpiar
                    </button>
                  </div>
                  <div 
                    className="border rounded-3" 
                    style={{ 
                      maxHeight: 320, 
                      overflowY: 'auto',
                      background: '#f8fafc'
                    }}
                  >
                    {selectedFiles.map((file, idx) => {
                      const sizeKB = (file.size / 1024).toFixed(1);
                      const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
                      const sizeDisplay = file.size > 1024 * 1024 ? `${sizeMB} MB` : `${sizeKB} KB`;
                      const path = file.webkitRelativePath || file.name;
                      
                      return (
                        <div
                          key={idx}
                          className="d-flex align-items-center gap-2 px-3 py-2"
                          style={{ 
                            borderBottom: idx < selectedFiles.length - 1 ? '1px solid #e2e8f0' : 'none',
                            background: '#ffffff'
                          }}
                        >
                          <i className="bi bi-file-earmark" style={{ color: '#64748b', fontSize: 18 }} />
                          <div className="flex-grow-1 overflow-hidden">
                            <div 
                              className="text-truncate fw-medium" 
                              style={{ color: '#1e293b', fontSize: 14 }}
                              title={path}
                            >
                              {path}
                            </div>
                            <div className="small" style={{ color: '#94a3b8' }}>
                              {sizeDisplay}
                            </div>
                          </div>
                          <button
                            className="btn btn-sm btn-link text-danger p-0"
                            onClick={() => setSelectedFiles(prev => prev.filter((_, i) => i !== idx))}
                            title="Quitar"
                          >
                            <i className="bi bi-x-lg" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Hidden file input - archivos individuales */}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />
              
              {/* Hidden folder input - carpetas completas */}
              <input
                ref={folderInputRef}
                type="file"
                multiple
                // @ts-ignore - webkitdirectory no está en los tipos oficiales pero funciona
                webkitdirectory=""
                directory=""
                style={{ display: 'none' }}
                onChange={handleFolderSelect}
              />
            </div>

            {/* Footer */}
            <div className="modal-footer" style={{ borderTop: '1px solid #e2e8f0' }}>
              {selectedFiles.length > 0 && (
                <span className="text-muted small me-auto">
                  {selectedFiles.length} archivo{selectedFiles.length > 1 ? 's' : ''} seleccionado{selectedFiles.length > 1 ? 's' : ''}
                </span>
              )}
              <button
                type="button"
                className="btn btn-feebbo-outline"
                onClick={() => {
                  setSelectedFiles([]);
                  onClose();
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-feebbo"
                onClick={handleUploadClick}
                disabled={selectedFiles.length === 0}
              >
                <i className="bi bi-cloud-upload me-2" />
                Subir
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
